import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { ChevronLeft, ChevronRight, Download, ExternalLink, Film, ImageOff, Loader2, Maximize2, Minimize2, Play, X } from 'lucide-react';
import { API_PREFIX, previewKind, type AttachmentView } from '@vwt/contracts';
import { cn } from '@vwt/ui';
import { apiUrl } from '../lib/api-base.js';
import { formatBytes } from '../lib/format.js';
import { useTranslation } from '../lib/i18n.js';

export type MediaKind = 'image' | 'video';

/** One file the viewer can show. `url` is server-relative; apiUrl() makes it absolute. */
export interface MediaItem {
  id: string;
  filename: string;
  contentType: string;
  byteSize: number;
  kind: MediaKind;
  url: string;
  uploadedBy: string | null;
}

/** Images and videos open in the viewer; PDFs keep opening through the browser (the content CSP sandboxes them). */
export function mediaKind(contentType: string): MediaKind | null {
  const kind = previewKind(contentType);
  return kind === 'image' || kind === 'video' ? kind : null;
}

/** The fields the viewer needs; AttachmentView and comment attachments both satisfy it. */
export interface ViewableFile {
  id: string;
  filename: string;
  contentType: string;
  byteSize: number;
  scanState: AttachmentView['scanState'];
  uploadedBy?: { displayName: string } | null;
}

/** The viewer item for an available image or video; null for pending, rejected or download-only files. */
export function toMediaItem(file: ViewableFile): MediaItem | null {
  const kind = mediaKind(file.contentType);
  if (!kind || file.scanState !== 'AVAILABLE') return null;
  return {
    id: file.id,
    filename: file.filename,
    contentType: file.contentType,
    byteSize: file.byteSize,
    kind,
    url: `${API_PREFIX}/attachments/${file.id}/content`,
    uploadedBy: file.uploadedBy?.displayName ?? null,
  };
}

/** Every viewable file of a list, in order; the viewer navigates within it. */
export function mediaItemsOf(files: ViewableFile[]): MediaItem[] {
  return files.map(toMediaItem).filter((item): item is MediaItem => item !== null);
}

interface ViewerApi {
  /** Opens the viewer on `items[index]`; the whole list stays reachable with the arrows. */
  open: (items: MediaItem[], index?: number) => void;
}

const MediaViewerContext = createContext<ViewerApi | null>(null);

/** Without a provider (isolated renders) the file opens in a browser tab instead. */
const fallbackApi: ViewerApi = {
  open: (items, index = 0) => {
    const item = items[index];
    if (item) window.open(apiUrl(item.url), '_blank', 'noopener');
  },
};

export function useMediaViewer(): ViewerApi {
  return useContext(MediaViewerContext) ?? fallbackApi;
}

/** Mounts one viewer for the whole app; any attachment list opens it through useMediaViewer(). */
export function MediaViewerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ items: MediaItem[]; index: number } | null>(null);
  const open = useCallback((items: MediaItem[], index = 0) => {
    if (!items.length) return;
    setState({ items, index: Math.min(Math.max(index, 0), items.length - 1) });
  }, []);
  const api = useMemo(() => ({ open }), [open]);
  return (
    <MediaViewerContext.Provider value={api}>
      {children}
      {state ? <MediaViewer items={state.items} index={state.index} onIndexChange={(index) => setState((s) => (s ? { ...s, index } : s))} onClose={() => setState(null)} /> : null}
    </MediaViewerContext.Provider>
  );
}

const toolbarClass = 'inline-flex size-9 shrink-0 items-center justify-center rounded-full text-white/80 hover:bg-white/15 hover:text-white disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4';

function ToolbarButton({ label, className, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return <button type="button" aria-label={label} title={label} className={cn(toolbarClass, className)} {...props} />;
}

function ToolbarLink({ label, className, ...props }: AnchorHTMLAttributes<HTMLAnchorElement> & { label: string }) {
  return <a aria-label={label} title={label} className={cn(toolbarClass, className)} {...props} />;
}

function NavButton({ side, label, onClick }: { side: 'left' | 'right'; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn('absolute top-1/2 z-10 inline-flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25', side === 'left' ? 'left-3' : 'right-3')}
    >
      {side === 'left' ? <ChevronLeft className="size-6" /> : <ChevronRight className="size-6" />}
    </button>
  );
}

/**
 * Full-screen viewer: images fit the screen and toggle to their actual size, videos play with the
 * browser's controls (the API answers Range requests, so seeking works), arrows and swipes move
 * between files, Escape or the dark backdrop closes.
 */
function MediaViewer({ items, index, onIndexChange, onClose }: { items: MediaItem[]; index: number; onIndexChange: (index: number) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const item = items[index]!;
  const many = items.length > 1;
  const src = apiUrl(item.url);
  const downloadHref = apiUrl(`${item.url}?download=1`);

  // Per-file outcome and zoom, keyed by id, so switching files never shows stale state.
  const [outcome, setOutcome] = useState<Record<string, 'ready' | 'error'>>({});
  const status = outcome[item.id] ?? 'loading';
  const mark = (id: string, result: 'ready' | 'error') => setOutcome((o) => (o[id] === result ? o : { ...o, [id]: result }));
  const [zoomedId, setZoomedId] = useState<string | null>(null);
  const zoomed = zoomedId === item.id;
  const toggleZoom = () => setZoomedId(zoomed ? null : item.id);

  const go = (delta: number) => {
    if (many) onIndexChange((index + delta + items.length) % items.length);
  };

  // Neighbouring images load ahead so the arrows feel instant.
  useEffect(() => {
    if (!many) return;
    for (const delta of [1, -1]) {
      const neighbour = items[(index + delta + items.length) % items.length]!;
      if (neighbour.kind === 'image') new Image().src = apiUrl(neighbour.url);
    }
  }, [items, index, many]);

  const touchStartX = useRef<number | null>(null);

  return (
    <DialogPrimitive.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="fixed inset-0 z-[60] flex flex-col text-white outline-none"
          data-testid="media-viewer"
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              go(-1);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              go(1);
            }
          }}
        >
          <DialogPrimitive.Title className="sr-only">{t('attachments.viewer')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">{t('attachments.viewerHint')}</DialogPrimitive.Description>

          <header className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium" title={item.filename}>
                {item.filename}
              </p>
              <p className="truncate text-xs text-white/60">{[many ? t('attachments.counter', { index: index + 1, total: items.length }) : null, formatBytes(item.byteSize), item.uploadedBy].filter(Boolean).join(' · ')}</p>
            </div>
            <div className="flex items-center gap-1">
              {item.kind === 'image' ? (
                <ToolbarButton label={zoomed ? t('attachments.fitToScreen') : t('attachments.actualSize')} onClick={toggleZoom} disabled={status !== 'ready'} aria-pressed={zoomed}>
                  {zoomed ? <Minimize2 /> : <Maximize2 />}
                </ToolbarButton>
              ) : null}
              <ToolbarLink href={src} target="_blank" rel="noreferrer" label={t('attachments.openInNewTab')}>
                <ExternalLink />
              </ToolbarLink>
              <ToolbarLink href={downloadHref} label={t('attachments.download')}>
                <Download />
              </ToolbarLink>
              <DialogPrimitive.Close asChild>
                <ToolbarButton label={t('common.close')}>
                  <X />
                </ToolbarButton>
              </DialogPrimitive.Close>
            </div>
          </header>

          <div
            className="relative min-h-0 flex-1"
            onTouchStart={(event) => {
              touchStartX.current = event.touches[0]?.clientX ?? null;
            }}
            onTouchEnd={(event) => {
              const start = touchStartX.current;
              touchStartX.current = null;
              const end = event.changedTouches[0]?.clientX;
              if (start === null || end === undefined) return;
              if (end - start > 60) go(-1);
              else if (start - end > 60) go(1);
            }}
          >
            {/* Auto margins keep a small file centred and a zoomed one scrollable from its top-left corner. */}
            <div
              className={cn('absolute inset-0 flex p-4', zoomed ? 'overflow-auto' : 'items-center justify-center')}
              onClick={(event) => {
                if (event.target === event.currentTarget) onClose();
              }}
            >
              {item.kind === 'image' ? (
                <img
                  key={item.id}
                  src={src}
                  alt={item.filename}
                  draggable={false}
                  onLoad={() => mark(item.id, 'ready')}
                  onError={() => mark(item.id, 'error')}
                  onClick={toggleZoom}
                  className={cn('select-none', zoomed ? 'm-auto max-w-none cursor-zoom-out' : 'max-h-full max-w-full cursor-zoom-in object-contain', status !== 'ready' && 'invisible')}
                />
              ) : (
                <video
                  key={item.id}
                  src={src}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  onLoadedData={() => mark(item.id, 'ready')}
                  onError={() => mark(item.id, 'error')}
                  className={cn('max-h-full max-w-full rounded-[var(--radius)] bg-black outline-none', status === 'error' && 'hidden')}
                />
              )}
            </div>
            {status === 'loading' ? <Loader2 className="pointer-events-none absolute top-1/2 left-1/2 size-8 -translate-x-1/2 -translate-y-1/2 animate-spin text-white/70" aria-hidden /> : null}
            {status === 'error' ? (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6">
                <div role="alert" className="pointer-events-auto flex max-w-sm flex-col items-center gap-3 rounded-[var(--radius-lg)] bg-white/10 px-6 py-5 text-center text-sm">
                  <ImageOff className="size-8 text-white/70" aria-hidden />
                  <p>{t('attachments.loadFailed')}</p>
                  <a href={downloadHref} className="rounded-[var(--radius)] bg-white px-3 py-1.5 text-xs font-medium text-black hover:bg-white/90">
                    {t('attachments.download')}
                  </a>
                </div>
              </div>
            ) : null}
            {many ? (
              <>
                <NavButton side="left" label={t('attachments.previous')} onClick={() => go(-1)} />
                <NavButton side="right" label={t('attachments.next')} onClick={() => go(1)} />
              </>
            ) : null}
          </div>

          {many ? (
            <nav className="flex justify-center gap-2 overflow-x-auto px-4 py-3" aria-label={t('attachments.title')}>
              {items.map((it, i) => (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onIndexChange(i)}
                  aria-label={it.filename}
                  aria-current={i === index ? 'true' : undefined}
                  title={it.filename}
                  className={cn('size-14 shrink-0 overflow-hidden rounded-[var(--radius-sm)] border-2 bg-white/5 transition-opacity', i === index ? 'border-white opacity-100' : 'border-transparent opacity-60 hover:opacity-100')}
                >
                  {it.kind === 'image' ? (
                    <img
                      src={apiUrl(it.url)}
                      alt=""
                      className="size-full object-cover"
                      loading="lazy"
                      draggable={false}
                      onError={(event) => {
                        event.currentTarget.style.visibility = 'hidden';
                      }}
                    />
                  ) : (
                    <span className="flex size-full items-center justify-center">
                      <Film className="size-5" aria-hidden />
                    </span>
                  )}
                </button>
              ))}
            </nav>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Clickable preview of an image or video. Videos show their first frame (the browser fetches it
 * with a Range request) under a play mark.
 */
export function MediaThumbnail({ item, onOpen, className, label }: { item: MediaItem; onOpen: () => void; className?: string; label?: string }) {
  const { t } = useTranslation();
  const src = apiUrl(item.url);
  // A file the browser cannot decode still opens the viewer, which offers the download.
  const [broken, setBroken] = useState(false);
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label ?? t('attachments.view', { name: item.filename })}
      title={item.filename}
      className={cn('group/thumb relative block overflow-hidden bg-muted text-fg-subtle focus-visible:outline-2 focus-visible:outline-ring', className)}
    >
      {broken ? (
        <span className="flex size-full items-center justify-center">
          {item.kind === 'image' ? <ImageOff className="size-6" aria-hidden /> : <Film className="size-6" aria-hidden />}
        </span>
      ) : item.kind === 'image' ? (
        <img src={src} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} className="size-full object-cover transition-transform duration-200 group-hover/thumb:scale-[1.03]" />
      ) : (
        <>
          <video src={`${src}#t=0.1`} preload="metadata" muted playsInline tabIndex={-1} aria-hidden onError={() => setBroken(true)} className="pointer-events-none size-full bg-black object-cover" />
          <span className="absolute inset-0 flex items-center justify-center bg-black/25 text-white transition-colors group-hover/thumb:bg-black/35">
            <span className="flex size-9 items-center justify-center rounded-full bg-black/60">
              <Play className="size-4 translate-x-px" fill="currentColor" aria-hidden />
            </span>
          </span>
        </>
      )}
    </button>
  );
}
